import { ref, type Ref } from 'vue';
import type { MessageApi } from 'naive-ui';
import type { Viewer } from '@puppycad/cad-core';

/**
 * 工具栏操作提示快照。
 */
interface ToolbarOperationSnapshot {
  /** 操作名称。 */
  name: string;
  /** 操作提示。 */
  hint: string;
}

/**
 * 评估工作流组合函数参数。
 */
export interface UseEditorEvaluationWorkflowOptions {
  /** 获取 Viewer 实例。 */
  getViewer: () => Viewer | null;
  /** 同步工具栏操作状态。 */
  syncToolbarOperationState: (
    command: string | null,
    operation: ToolbarOperationSnapshot | null,
  ) => void;
  /** 消息提示实例。 */
  message: MessageApi;
}

/**
 * 评估类工具栏命令集合。
 */
const EVALUATION_COMMANDS = new Set<string>([
  'measurement',
]);

/**
 * 评估工作流对外暴露的状态与动作。
 */
export interface EditorEvaluationWorkflow {
  /** 测量对话框是否可见。 */
  measureDialogVisible: Ref<boolean>;
  /** 当前激活的测量工具键。 */
  activeMeasureTool: Ref<string | null>;
  /** 测量对话框是否折叠。 */
  measureDialogCollapsed: Ref<boolean>;
  /** 判断是否为评估类命令。 */
  isEvaluationCommand: (commandType: string) => boolean;
  /** 处理评估类命令。 */
  handleEvaluationCommand: (commandType: string) => boolean;
  /** 关闭当前激活的评估会话。 */
  closeActiveEvaluationSessions: () => void;
  /** 处理评估类 Esc 键。 */
  handleEvaluationEscKey: () => boolean;
  /** 打开测量对话框。 */
  openMeasurement: () => void;
  /** 关闭测量对话框。 */
  closeMeasurement: () => void;
  /** 处理测量工具按钮点击。 */
  handleMeasureToolClick: (toolKey: string) => void;
  /** 处理测量对话框关闭。 */
  handleMeasureDialogClose: () => void;
  /** 处理测量对话框帮助。 */
  handleMeasureDialogHelp: () => void;
  /** 恢复测量拾取。 */
  resumeMeasurePick: () => void;
}

/**
 * 创建编辑器评估类命令工作流。
 *
 * 设计说明：
 * 1. 评估工具（测量、干涉检查等）属于 Inspection 会话，不写入模型 Undo；
 * 2. UI 状态集中在该 composable，Editor 只负责挂载浮层组件与命令分发；
 * 3. cad-core 测量能力通过 MeasureController 接入 start/stop 生命周期。
 *
 * @param options 工作流参数。
 * @returns 评估工作流控制器。
 */
export function useEditorEvaluationWorkflow(
  options: UseEditorEvaluationWorkflowOptions,
): EditorEvaluationWorkflow {
  /** 测量对话框是否可见。 */
  const measureDialogVisible = ref(false);
  /** 当前激活的测量工具键。 */
  const activeMeasureTool = ref<string | null>(null);
  /** 测量对话框是否折叠。 */
  const measureDialogCollapsed = ref(false);

  /**
   * 打开测量对话框并同步工具栏状态。
   */
  function openMeasurement(): void {
    const viewer = options.getViewer();
    if (!viewer) {
      options.message.error('Viewer 尚未初始化，暂时无法打开测量对话框。');
      return;
    }
    const started = viewer.enterMeasureMode();
    if (!started) {
      options.message.error('无法启动测量会话。');
      return;
    }
    viewer.measureController.resumePick();
    measureDialogVisible.value = true;
    activeMeasureTool.value = 'arc-circle';
    options.syncToolbarOperationState('measurement', {
      name: '测量',
      hint: '选择要测量的实体。',
    });
  }

  /**
   * 关闭测量对话框并清理 UI 状态。
   */
  function closeMeasurement(): void {
    options.getViewer()?.exitMeasureMode();
    if (!measureDialogVisible.value && activeMeasureTool.value === null) {
      return;
    }
    measureDialogVisible.value = false;
    activeMeasureTool.value = null;
    options.syncToolbarOperationState(null, null);
  }

  /**
   * 处理测量工具按钮点击。
   *
   * @param toolKey 工具键。
   */
  /**
   * 恢复测量拾取（点击 Measure 对话框时触发）。
   */
  function resumeMeasurePick(): void {
    options.getViewer()?.measureController.resumePick();
  }

  function handleMeasureToolClick(toolKey: string): void {
    const viewer = options.getViewer();
    if (!viewer?.measureController.isMeasureActive()) {
      return;
    }
    viewer.measureController.resumePick();
    viewer.measureController.applyToolbarCommand(toolKey);

    if (toolKey === 'display-xyz') {
      activeMeasureTool.value = viewer.measureController.isDxDyDzMeasurementEnabled()
        ? 'display-xyz'
        : null;
      return;
    }

    activeMeasureTool.value = toolKey;
  }

  /**
   * 处理测量对话框关闭事件。
   */
  function handleMeasureDialogClose(): void {
    closeMeasurement();
  }

  /**
   * 处理测量对话框帮助事件。
   */
  function handleMeasureDialogHelp(): void {
    options.message.info('测量工具帮助（待实现）');
  }

  /**
   * 判断命令是否属于评估工作流。
   *
   * @param commandType 命令标识。
   * @returns 是否为评估类命令。
   */
  function isEvaluationCommand(commandType: string): boolean {
    return EVALUATION_COMMANDS.has(commandType);
  }

  /**
   * 分发评估类命令。
   *
   * @param commandType 命令标识。
   * @returns 是否已由评估工作流处理。
   */
  function handleEvaluationCommand(commandType: string): boolean {
    if (commandType === 'measurement') {
      openMeasurement();
      return true;
    }
    return false;
  }

  /**
   * 关闭当前所有评估会话。
   *
   * 设计说明：切换到特征/草图/装配体命令前必须收口评估 UI，避免浮层与命令态并行残留。
   */
  function closeActiveEvaluationSessions(): void {
    closeMeasurement();
  }

  /**
   * 处理评估类 Esc 键。
   *
   * @returns 是否已消费 Esc 事件。
   */
  function handleEvaluationEscKey(): boolean {
    if (!measureDialogVisible.value) {
      return false;
    }
    closeMeasurement();
    options.message.info('已关闭测量');
    return true;
  }

  return {
    measureDialogVisible,
    activeMeasureTool,
    measureDialogCollapsed,
    isEvaluationCommand,
    handleEvaluationCommand,
    closeActiveEvaluationSessions,
    handleEvaluationEscKey,
    openMeasurement,
    closeMeasurement,
    handleMeasureToolClick,
    handleMeasureDialogClose,
    handleMeasureDialogHelp,
    resumeMeasurePick,
  };
}
